import { Video, Star, Users, Crown, User, Wallet, Clock } from 'lucide-react';
import { useExpertDashboard } from '../../hooks/expertDashboardHooks/useExpertDashboard';
import { useState, useEffect } from 'react';
import { useExpertSocket } from '../../connectivity/chat/ExpertSocketProvider';
import { connectivityAPI } from '../../api/connectivityApis/connectivity.api';

// 🟢 FIXED: Created a local, crash-proof Rupee icon that perfectly matches Lucide's style
const IndianRupeeIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M6 3h12" />
    <path d="M6 8h12" />
    <path d="m6 13 8.5 8" />
    <path d="M6 13h3" />
    <path d="M9 13c6.667 0 6.667-10 0-10" />
  </svg>
);

const STAT_CARD_CONFIG = [
  { label: "Total Earnings", value: "₹0", change: "0%", icon: IndianRupeeIcon, color: "bg-green-100 text-green-600" },
  { label: "Total Sessions", value: "0", change: "0%", icon: Video, color: "bg-blue-100 text-blue-600" },
  { label: "Client Rating", value: "0/5", change: "0", icon: Star, color: "bg-yellow-100 text-yellow-600" },
  { label: "Active Clients", value: "0", change: "0", icon: Users, color: "bg-purple-100 text-purple-600" },
];

// Helper to format seconds into "Xm Ys"
const formatDuration = (totalSeconds: number) => {
  if (!totalSeconds) return null;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
};

export default function ExpertDashboardHome() {
  const { dashboardData, earningsData, fetchFilteredEarnings } = useExpertDashboard();
  const { socket } = useExpertSocket();
  
  const [activeFilter, setActiveFilter] = useState<string>('7days');
  
  const [premiumQueue, setPremiumQueue] = useState<any[]>([]);
  const [standardQueue, setStandardQueue] = useState<any[]>([]);

  const welcomeName = dashboardData?.expertName || localStorage.getItem('expertName')?.split(' ')[0] || "Expert";
  
  const currentStats = STAT_CARD_CONFIG.map(stat => {
    const liveStat = dashboardData?.stats?.find((ds: any) => ds.label === stat.label);
    return { ...stat, value: liveStat ? liveStat.value : stat.value };
  });

  // Load Initial Queue Data
  useEffect(() => {
    const code = localStorage.getItem('expertCode') || localStorage.getItem('expert_code');
    if (code) {
     
      connectivityAPI.getExpertQueueData(code)
        .then(data => {
          setPremiumQueue(data.premiumQueue || []);
          setStandardQueue(data.standardQueue || []);
        })
        .catch(err => console.error(`[Dashboard] Failed to fetch queue data`, err));
    }
  }, []);

  // Listen for Socket Updates from Backend
  useEffect(() => {
    if (!socket) return;

    const handleQueueUpdate = (data: any) => {
   
      setPremiumQueue(data.premiumQueue || []);
      setStandardQueue(data.standardQueue || []);
    };

    socket.on('expert_dashboard_queue_update', handleQueueUpdate);

    return () => {
      socket.off('expert_dashboard_queue_update', handleQueueUpdate);
    };
  }, [socket]);

  return (
    <div className="p-4 md:p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Welcome back, {welcomeName}! 👋</h2>
          <p className="text-gray-500">Consultations overview.</p>
        </div>
        {dashboardData?.walletBalance !== undefined && (
           <div className="flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-lg border border-purple-100 font-bold">
              <Wallet className="w-5 h-5" />
              <span>Wallet: ₹{dashboardData.walletBalance.toFixed(2)}</span>
           </div>
        )}
      </div>
      
      {/* REAL STATS RENDERED HERE */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {currentStats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className={`p-3 rounded-lg w-fit mb-4 ${stat.color}`}>
              <stat.icon className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{stat.value}</h3>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* EARNINGS & TRANSACTIONS SECTION */}
        <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-[500px]">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-bold text-lg">Earnings History</h3>
            
            <select 
              className="text-sm border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border bg-white font-medium"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value); 
                fetchFilteredEarnings(e.target.value); 
              }}
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="1month">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
          
          {/* Earnings Summary for the selected period */}
          <div className="grid grid-cols-2 divide-x divide-gray-100 border-b border-gray-100 bg-slate-50">
             <div className="p-4 text-center">
               <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Period Earnings</p>
               <p className="text-2xl font-bold text-green-600">
                 ₹{earningsData?.summary?.total_earned?.toFixed(2) || '0.00'}
               </p>
             </div>
             <div className="p-4 text-center">
               <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Period Sessions</p>
               <p className="text-2xl font-bold text-blue-600">
                 {earningsData?.summary?.total_sessions || 0}
               </p>
             </div>
          </div>

          {/* Transactions List */}
          <div className="flex-1 overflow-y-auto">
            {!earningsData?.transactions || earningsData.transactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-2 text-gray-400">
                 {/* 🟢 FIXED: Using the local, crash-proof Rupee icon here as well */}
                 <IndianRupeeIcon className="w-10 h-10 opacity-20" />
                 <p className="text-sm italic">No transactions in this period.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {earningsData.transactions.map((txn: any) => (
                  <div key={txn.id} className="p-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    
                    {/* LEFT SIDE: Details, Badges, Category */}
                    <div className="flex flex-col">
                      <p className="font-bold text-gray-800">
                        {txn.transaction_type === 'session_earning' 
                          ? `${txn.category_name || 'General'} Consultation` 
                          : txn.description}
                      </p>

                      {/* Render Badges if this is a session earning */}
                      {txn.transaction_type === 'session_earning' && (
                        <div className="flex items-center gap-2 mt-1.5">
                          {txn.mode === 'chat' && <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">💬 Chat</span>}
                          {txn.mode === 'call' && <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">📞 Call</span>}
                          {txn.mode === 'video' && <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">🎥 Video</span>}

                          {txn.duration && (
                            <span className="bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded flex items-center gap-1 border border-gray-200">
                              <Clock className="w-3 h-3" />
                              {formatDuration(txn.duration)}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center text-xs text-gray-500 mt-1.5 gap-3">
                        <span>{new Date(txn.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                        {txn.session_id && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded text-[10px]">
                            Ref: {txn.session_id.split('-')[0]}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* RIGHT SIDE: The Money */}
                    <div className="text-right">
                      <p className={`font-bold text-lg ${txn.transaction_type === 'withdrawal' ? 'text-gray-800' : 'text-green-600'}`}>
                        {txn.transaction_type === 'withdrawal' ? '-' : '+'}₹{Math.abs(parseFloat(txn.amount)).toFixed(2)}
                      </p>
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* LIVE WAITING ROOM WIDGET */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-[500px]">
          <div className="p-6 border-b border-gray-100">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              Live Waiting Room
            </h3>
            <p className="text-sm text-gray-500 mt-1">Users currently waiting for you</p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-slate-50">
            
            {/* VIP Lane */}
            <div>
              <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Crown className="w-4 h-4" /> VIP Priority Lane ({premiumQueue.length})
              </h4>
              {premiumQueue.length === 0 ? (
                <div className="text-sm text-slate-400 italic px-2">No VIP clients waiting.</div>
              ) : (
                <div className="space-y-2">
                  {premiumQueue.map((user, idx) => (
                    <div key={user.userCode || idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3 shadow-sm">
                      <div className="w-8 h-8 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center font-bold text-sm">
                        #{idx + 1}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{user.name}</p>
                        <p className="text-xs text-amber-600 font-medium">{user.userCode}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Standard Lane */}
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Standard Lane ({standardQueue.length})
              </h4>
              {standardQueue.length === 0 ? (
                <div className="text-sm text-slate-400 italic px-2">No standard clients waiting.</div>
              ) : (
                <div className="space-y-2">
                  {standardQueue.map((user, idx) => (
                    <div key={user.userCode || idx} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 shadow-sm">
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-sm">
                        #{premiumQueue.length + idx + 1}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.userCode}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}